/* WAGILO Atelier 2.0 — homepage interactions */
(() => {
  'use strict';
  if (window.__wagiloHomeLoaded) return;
  window.__wagiloHomeLoaded = true;

  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)');

  function initCardMedia(root = document) {
    qa('[data-wg-card-media]', root).forEach(media => {
      if (media.dataset.wgMediaReady || !q('.wg-card-image--secondary', media)) return;
      media.dataset.wgMediaReady = 'true';
      let startX = 0;
      media.addEventListener('pointerdown', event => { startX = event.clientX; });
      media.addEventListener('pointerup', event => {
        if (!coarsePointer.matches) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 24 || Math.abs(distance) < 8) {
          event.preventDefault();
          media.classList.toggle('is-secondary');
        }
      });
      media.addEventListener('click', event => {
        if (coarsePointer.matches) event.preventDefault();
      });
    });
  }

  function initFilm(root = document) {
    qa('[data-wg-film-section]', root).forEach(section => {
      const toggle = q('[data-wg-film-toggle]', section);
      if (!toggle || toggle.dataset.wgFilmReady) return;
      toggle.dataset.wgFilmReady = 'true';
      toggle.addEventListener('click', () => {
        const video = q('video', section);
        const active = section.classList.toggle('is-previewing');
        toggle.setAttribute('aria-pressed', String(active));
        if (video) active ? video.play().catch(() => {}) : video.pause();
      });
    });
  }

  const drawer = q('[data-wg-cart-drawer]');
  const backdrop = q('.wg-cart-drawer-backdrop');

  function setDrawer(open) {
    if (!drawer || !backdrop) return;
    drawer.classList.toggle('is-open', open);
    backdrop.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('wg-drawer-open', open);
    if (open) q('[data-wg-cart-close]', drawer)?.focus();
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (_) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function updateCartCount(count) {
    const link = q('[data-wg-cart-open]');
    if (!link) return;
    let badge = q('.wg-cart-count', link);
    if (!badge && count > 0) {
      badge = document.createElement('span');
      badge.className = 'wg-cart-count';
      link.appendChild(badge);
    }
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    }
    link.setAttribute('aria-label', `Shopping bag (${count})`);
  }

  function renderCart(cart) {
    const body = q('[data-wg-cart-body]');
    const total = q('[data-wg-cart-total]');
    if (!body) return;
    body.replaceChildren();
    if (!cart.items.length) {
      const empty = document.createElement('div');
      empty.className = 'wg-cart-drawer__empty';
      const strong = document.createElement('strong');
      strong.textContent = 'Your bag is empty.';
      const span = document.createElement('span');
      span.textContent = 'Explore the collection to begin.';
      empty.append(strong, span);
      body.appendChild(empty);
    } else {
      cart.items.forEach(item => {
        const article = document.createElement('article');
        article.className = 'wg-cart-drawer__item';
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.product_title;
          article.appendChild(image);
        }
        const copy = document.createElement('div');
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = item.product_title;
        const detail = document.createElement('small');
        detail.textContent = `${item.quantity} × ${money(item.final_price, cart.currency)}`;
        copy.append(link, detail);
        article.appendChild(copy);
        body.appendChild(article);
      });
    }
    if (total) total.textContent = money(cart.total_price, cart.currency);
    updateCartCount(cart.item_count);
  }

  async function refreshCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart refresh failed');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  function initCart(root = document) {
    qa('[data-wg-cart-open]', root).forEach(open => {
      if (open.dataset.wgCartReady) return;
      open.dataset.wgCartReady = 'true';
      open.addEventListener('click', event => { event.preventDefault(); setDrawer(true); });
    });
    qa('[data-wg-cart-close]', root).forEach(close => {
      if (close.dataset.wgCartReady) return;
      close.dataset.wgCartReady = 'true';
      close.addEventListener('click', () => setDrawer(false));
    });
    qa('.wg-card-form', root).forEach(form => {
      if (form.dataset.wgQuickReady) return;
      form.dataset.wgQuickReady = 'true';
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = q('button[type="submit"]', form);
        if (!button || button.disabled) return;
        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';
        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Add to cart failed');
          await refreshCart();
          setDrawer(true);
          button.textContent = 'Added';
        } catch (_) {
          form.submit();
          return;
        }
        window.setTimeout(() => { button.disabled = false; button.textContent = original; }, 1200);
      });
    });
  }

  function init(root = document) {
    initCardMedia(root);
    initFilm(root);
    initCart(root);
  }

  document.addEventListener('keydown', event => { if (event.key === 'Escape') setDrawer(false); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
